import { useMemo } from 'react';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import type { RefundDrawerRow } from '../types';
import type { ReturnOrderListItemV2 } from 'services/api/refund';

export interface UseRefundDrawerBodyParams {
  trade: Conversation.Trade.Vo | undefined;
  platform: General.Platform;
  returnSn: string;
  row: RefundDrawerRow;
  statusTip: { date: string; money: string; platform: string };
  isRefund: boolean;
}

export interface RefundDrawerBodyStatusContent {
  title: string;
  description?: string;
}

export interface UseRefundStatusContentParams {
  platform: General.Platform;
  displayStatus: string;
  dueDateMs: string;
  unit: string;
  partiallyRefundAmount: string;
  statusTip: { date: string; money: string; platform: string };
  isRefund: boolean;
}

export const useRefundStatusContent = (props: UseRefundStatusContentParams) => {
  const { t } = useTranslate();

  const { platform, displayStatus, dueDateMs, unit, partiallyRefundAmount, statusTip, isRefund } =
    props;

  const statusContent = useMemo((): RefundDrawerBodyStatusContent | null => {
    if (platform === 'mercado') {
      const title = t(`mercado.returnStatus.${displayStatus}`);
      const description =
        displayStatus === 'REVERSE_PENDING' && !Number(dueDateMs)
          ? undefined
          : t(`mercado.returnDescription.${displayStatus}`, {
              date: dayjs(Number(dueDateMs)).format('YYYY-MM-DD'),
              amount: `${unit}${partiallyRefundAmount}`,
            });
      return { title, description };
    }
    if (platform !== 'lazada' && isRefund) {
      const shopeeDisplayStatus = displayStatus as Conversation.Trade.ShopeeDisplayStatus;
      const title = {
        IN_REVIEW: t('状态展示标题枚举.IN_REVIEW'),
        WAITING_EVIDENCE: t('状态展示标题枚举.WAITING_EVIDENCE'),
        RETURN_PROCESSING: t('状态展示标题枚举.RETURN_PROCESSING'),
        PENDING_RESPOND: t('状态展示标题枚举.PENDING_RESPOND', {
          money: statusTip.money,
        }),
        REFUND_APPROVED: t('状态展示标题枚举.REFUND_APPROVED'),
        WAITING_BUYER_RESPONSE: t('状态展示标题枚举.WAITING_BUYER_RESPONSE', {
          money: statusTip.money,
        }),
        RETURN_IN_PROGRESS: t('状态展示标题枚举.RETURN_IN_PROGRESS'),
        REFUND_COMPLETED: t('状态展示标题枚举.REFUND_COMPLETED'),
        DISPUTE_RESOLVED: t('状态展示标题枚举.DISPUTE_RESOLVED'),
        CANCELLED: t('状态展示标题枚举.CANCELLED'),
        UNKNOWN: t('状态展示标题枚举.UNKNOWN', { platform: 'Shopee' }),
        REFUND_PENDING: t('状态展示标题枚举.REFUND_PENDING', {
          money: statusTip.money,
        }),
        RETURN_REFUND_PENDING: t('状态展示标题枚举.RETURN_REFUND_PENDING', {
          money: statusTip.money,
        }),
        REFUND_REQUESTED: t('状态展示标题枚举.REFUND_REQUESTED', {
          money: statusTip.money,
        }),
        VALIDATION_IN_PROGRESS: t('状态展示标题枚举.VALIDATION_IN_PROGRESS'),
        VALIDATION_IN_PROGRESS_V2: t('状态展示标题枚举.VALIDATION_IN_PROGRESS'),
        EVIDENCE_SUBMITTED: t('状态展示标题枚举.EVIDENCE_SUBMITTED'),
        COMPENSATION_APPROVED: t('状态展示标题枚举.COMPENSATION_APPROVED'),
        DISPUTE_REJECT: t('状态展示标题枚举.DISPUTE_REJECT'),
        DISPUTE_WAIT_EVIDENCE: t('状态展示标题枚举.DISPUTE_WAIT_EVIDENCE'),
        REFUND_APPROVED_2: t('状态展示标题枚举.RETURN_IN_PROGRESS'),
      }[shopeeDisplayStatus];
      const description = {
        IN_REVIEW: t('状态展示枚举描述.IN_REVIEW'),
        WAITING_EVIDENCE: t('状态展示枚举描述.WAITING_EVIDENCE', {
          date: statusTip.date,
        }),
        RETURN_PROCESSING: t('状态展示枚举描述.RETURN_PROCESSING', {
          date: statusTip.date,
        }),
        PENDING_RESPOND: t('状态展示枚举描述.PENDING_RESPOND', {
          money: statusTip.money,
          date: statusTip.date,
        }),
        REFUND_APPROVED: t('状态展示枚举描述.REFUND_APPROVED', {
          money: statusTip.money,
          date: statusTip.date,
        }),
        WAITING_BUYER_RESPONSE: t('状态展示枚举描述.WAITING_BUYER_RESPONSE'),
        RETURN_IN_PROGRESS: t('状态展示枚举描述.RETURN_IN_PROGRESS'),
        REFUND_COMPLETED: t('状态展示枚举描述.REFUND_COMPLETED', {
          money: statusTip.money,
        }),
        DISPUTE_RESOLVED: t('状态展示枚举描述.DISPUTE_RESOLVED'),
        CANCELLED: t('状态展示枚举描述.CANCELLED'),
        UNKNOWN: t('状态展示枚举描述.UNKNOWN', { platform: 'Shopee' }),
        REFUND_PENDING: t('状态展示枚举描述.REFUND_PENDING', {
          date: statusTip.date,
        }),
        RETURN_REFUND_PENDING: t('状态展示枚举描述.RETURN_REFUND_PENDING', {
          date: statusTip.date,
        }),
        REFUND_REQUESTED: t('状态展示枚举描述.REFUND_REQUESTED', {
          date: statusTip.date,
        }),
        VALIDATION_IN_PROGRESS: t('状态展示枚举描述.VALIDATION_IN_PROGRESS', {
          date: statusTip.date,
        }),
        VALIDATION_IN_PROGRESS_V2: t('商品已到达仓库，验证后您将收到通知。'),
        EVIDENCE_SUBMITTED: t('状态展示枚举描述.EVIDENCE_SUBMITTED'),
        COMPENSATION_APPROVED: t('状态展示枚举描述.COMPENSATION_APPROVED', {
          money: statusTip.money,
        }),
        DISPUTE_REJECT: t('状态展示枚举描述.DISPUTE_REJECT'),
        DISPUTE_WAIT_EVIDENCE: t('状态展示枚举描述.DISPUTE_WAIT_EVIDENCE', {
          date: statusTip.date,
        }),
        REFUND_APPROVED_2: t('状态展示枚举描述.RETURN_IN_PROGRESS'),
      }[shopeeDisplayStatus];

      if (!title) return null;

      return { title, description };
    }
    return null;
  }, [
    platform,
    isRefund,
    t,
    displayStatus,
    dueDateMs,
    unit,
    partiallyRefundAmount,
    statusTip.money,
    statusTip.date,
  ]);

  return { statusContent };
};

/**
 * 退款抽屉 Body 内派生数据与状态文案。
 * 从 trade + dataSource + row 计算：退款商品列表、原因原始数据、展示订单号、状态区块文案。
 */
export function useRefundDrawerBody({
  trade,
  platform,
  returnSn,
  row,
  statusTip,
  isRefund,
}: UseRefundDrawerBodyParams) {
  const returnRow = row as ReturnOrderListItemV2;
  const { displayStatus, dueDateMs, unit = '', partiallyRefundAmount = '' } = returnRow;

  const { statusContent } = useRefundStatusContent({
    platform,
    displayStatus,
    dueDateMs,
    unit,
    partiallyRefundAmount,
    statusTip,
    isRefund,
  });

  const { refundItems, reasonRaw, displayTradeId, tradeItems } = useMemo(() => {
    if (!trade) {
      return {
        refundItems: [] as any[],
        reasonRaw: null as any,
        displayTradeId: '',
        tradeItems: [] as any[],
      };
    }
    const { items: itemsFromTrade = [], externalTradeId, invoiceNumber } = trade;
    const displayTradeId = platform === 'tokopedia' ? invoiceNumber : externalTradeId;
    const refundItems = itemsFromTrade
      .filter((it) => it.refundReason?.some((reason) => reason.returnSn === returnSn))
      .map(({ refundReason, ...others }) => ({
        ...others,
        refundReason: refundReason?.filter((_) => _.returnSn === returnSn),
      }));
    const reasonRaw = refundItems[0]?.refundReason
      ? refundItems[0].refundReason.find((r) => r.returnSn === returnSn) ?? null
      : null;
    return {
      refundItems,
      reasonRaw,
      displayTradeId: displayTradeId ?? '',
      tradeItems: itemsFromTrade,
    };
  }, [trade, platform, returnSn]);

  return {
    refundItems,
    reasonRaw,
    displayTradeId,
    tradeItems,
    statusContent,
  };
}
